/**
 * The admin HTTP client.
 *
 * Thinner than the Mini App's, because the admin surface needs less: there is
 * no idempotency plumbing here and that is deliberate rather than an omission.
 * The two writes where a double-click would be expensive are already protected
 * where it counts — `approve` posts under `deposit:{id}`, so a retry credits
 * once no matter how many requests arrive, and a second approval from the same
 * admin collides with `UNIQUE (deposit_id, admin_id)`. A client-supplied key
 * would add a second, weaker mechanism on top of natural keys that already hold
 * across different clients and different people.
 */

import { getToken, clearToken, getTrustedDevice, setTrustedDevice } from '../auth.ts';
import type {
  AdminDepositDetailView,
  AdminDepositView,
  AdminIpAllowlistEntryView,
  AgentView,
  AgentBotView,
  AgentCollectionView,
  AdminPaymentDestinationView,
  AdminPlayerDetailView,
  AdminPlayerView,
  AdminView,
  NotificationCounts,
  ApiErrorBody,
  AuditEntityType,
  AuditEntry,
  AdminWithdrawalView,
  BankMethod,
  BroadcastView,
  LoginResponse,
  MediaAssetKind,
  MediaAssetView,
  PayoutAttemptView,
  ReconcileReport,
  RevenueReport,
  RoundView,
  RoundListItemView,
  DrawResult,
  WinningPattern,
  GameTypeView,
  UpdateGameTypeInput,
  SupportThreadView,
  SupportMessageView,
  EnrolmentCodeView,
  MintedEnrolmentCode,
} from './types.ts';

const BASE = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '/api';

/**
 * How long a plain JSON call is allowed to take. Every one of these answers in
 * milliseconds on a healthy link, so 30s is a floor, not a target.
 */
const REQUEST_TIMEOUT_MS = 30_000;

/** A thumbnail download is a few MB of bytes; give it longer than a JSON reply. */
const BLOB_TIMEOUT_MS = 60_000;

/**
 * Aborts a fetch that hasn't settled within `ms`. Every call in this file
 * used to be a bare `fetch`: a request that never resolves and never rejects —
 * which is exactly what a half-dead CGNAT socket does (2026-09-09) — left
 * `busy` true forever and froze the console until a manual page reload.
 */
function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit,
  ms: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), ms);
  return fetch(input, { ...init, signal: controller.signal }).finally(() => window.clearTimeout(timer));
}

/**
 * A transport failure is either an aborted-when-timed-out request
 * (`DOMException` AbortError) or a connection death (fetch rejects with
 * `TypeError`) — as opposed to an HTTP status, which is an answer.
 */
function isTransportError(err: unknown): err is DOMException | TypeError {
  return err instanceof DOMException || err instanceof TypeError;
}

/**
 * Uploads get a budget proportional to file size — the operator's uplink has
 * been seen to take ~1 minute for a 2MB poster. Bounded regardless (assume at
 * least ~32 KB/s), so a completely dead socket still surfaces within a few
 * minutes instead of freezing the page forever.
 */
function uploadTimeoutMs(bytes: number): number {
  return Math.min(240_000, Math.max(90_000, Math.round(bytes / 32_000) * 1_000));
}

/**
 * Shared by upload and replace. Retries **once** on a transport failure only:
 * a fresh socket usually clears the exact thing that killed the first attempt,
 * which is the operator's repeated live experience. An HTTP 4xx/5xx is a real
 * answer and is never retried — a retry could double-row an upload whose 201
 * got lost mid-flight, and everyone who uses that endpoint can see and delete
 * a stray duplicate, which is the cheaper mistake.
 */
async function postMultipart(
  url: string,
  headers: Record<string, string>,
  form: FormData,
): Promise<Response> {
  const file = form.get('file');
  const budget = uploadTimeoutMs(file instanceof File ? file.size : 0);
  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      return await fetchWithTimeout(
        url,
        { method: 'POST', headers, body: form },
        budget,
      );
    } catch (err) {
      lastError = err;
      if (!isTransportError(err)) throw err;
    }
  }
  throw new ApiError(0, null, 'could not reach the API — the connection dropped; check it and try again');
}

/**
 * Resolves a server-issued path against the API base.
 *
 * `signProofUrl` mints `/admin/proofs/:id?exp=…&sig=…` — rooted at the *API*,
 * which is not where this app is served from. Dropping that straight into an
 * `<img src>` asks the admin origin for it, which 404s: in development because
 * Vite only proxies `/api`, and in production because the console is static
 * files that know nothing about admin routes.
 *
 * Every `fetch` in this file goes through `BASE` already. This exists because an
 * `<img>` does not, and it is the one place a URL escapes the client without
 * passing through `request()`.
 */
export function apiUrl(path: string): string {
  return BASE + path;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    // Unlike the player client, the server's own message is shown as-is. These
    // messages were written for operators — "deposit … cannot move approved ->
    // rejected" is exactly what a reviewer needs, and translating it into
    // reassuring prose would hide the state machine from the people whose job
    // is to reason about it.
    super(body?.error?.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'network_error';
    this.details = body?.error?.details;
  }

  /** The session is gone; the shell drops back to the login screen. */
  get isAuthFailure(): boolean {
    return this.status === 401;
  }

  /** Authenticated, but this role may not do it — §7.6's scoped roles. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Login is the one call that must not send (or clear) a token. */
  anonymous?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  // The trusted-device capability rides on every request (login included) so
  // the IP allowlist recognizes this browser on any network (§7.6).
  const trustedDevice = getTrustedDevice();
  if (trustedDevice) headers['x-trusted-device'] = trustedDevice;

  if (!options.anonymous) {
    const token = getToken();
    if (!token) throw new ApiError(401, null, 'not signed in');
    headers['authorization'] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetchWithTimeout(
      BASE + path,
      {
        method: options.method ?? 'GET',
        headers,
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      },
      REQUEST_TIMEOUT_MS,
    );
  } catch {
    throw new ApiError(0, null, 'could not reach the API');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    const error = new ApiError(response.status, parsed as ApiErrorBody | null, response.statusText);
    // A dead session should not linger in storage waiting to be sent again on
    // every subsequent request. Clearing here means one 401 anywhere returns
    // the whole console to the login screen.
    if (error.isAuthFailure && !options.anonymous) clearToken();
    throw error;
  }
  // The server may renew the trusted-device capability on any response — most
  // notably when an authenticated session self-heals a lost/expired device
  // token. Persist it so the very next request carries it and the IP check is
  // skipped (§7.6, trustedDevice.ts).
  renewTrustedDeviceFrom(response);
  return parsed as T;
}

/**
 * Persists an `x-trusted-device` response header if the server renewed the
 * capability. Idempotent and cheap; a header is simply absent on ordinary
 * responses.
 */
function renewTrustedDeviceFrom(response: Response): void {
  const renewed = response.headers.get('x-trusted-device');
  if (renewed) setTrustedDevice(renewed);
}

/**
 * `GET /admin/reconcile` answers **503** when the ledger has drifted, and that
 * is a successful call reporting a real result, not a transport failure.
 * Treating it as an error would turn the one endpoint designed to page someone
 * into "something went wrong, try again".
 */
async function reconcile(): Promise<ReconcileReport> {
  try {
    return await request<ReconcileReport>('/admin/reconcile');
  } catch (err) {
    if (err instanceof ApiError && err.status === 503 && isReport(err.details)) {
      return err.details;
    }
    throw err;
  }
}

function isReport(value: unknown): value is ReconcileReport {
  return typeof value === 'object' && value !== null && 'drift' in value;
}

export interface CreateDestinationInput {
  method: BankMethod;
  label: string;
  account: string;
  accountName?: string;
  instructions?: string;
  minAmount?: string;
  maxAmount?: string;
  sortOrder?: number;
}

export interface UpdateDestinationInput {
  method?: BankMethod;
  label?: string;
  account?: string;
  /** `null` clears a previously-set value. */
  accountName?: string | null;
  instructions?: string | null;
  minAmount?: string | null;
  maxAmount?: string | null;
  sortOrder?: number;
}

export const api = {
  // --- session -------------------------------------------------------------

  login: (
    username: string,
    password: string,
    totpCode: string,
    enrolmentCode?: string,
  ): Promise<LoginResponse> =>
    request('/admin/login', {
      method: 'POST',
      anonymous: true,
      body: enrolmentCode
        ? { username, password, totpCode, enrolmentCode }
        : { username, password, totpCode },
    }),

  logout: (): Promise<void> => request('/admin/logout', { method: 'POST' }),

  me: (): Promise<AdminView> => request('/admin/me'),

  /** Nav-badge counts — what is waiting, without opening each tab. */
  notificationCounts: (): Promise<NotificationCounts> => request('/admin/notification-counts'),

  /** Clears this admin's inbox badge. Called when the Inbox screen loads. */
  markInboxSeen: (): Promise<void> => request('/admin/inbox/seen', { method: 'POST' }),

  // --- deposits ------------------------------------------------------------

  pendingDeposits: (): Promise<{ deposits: AdminDepositView[] }> =>
    request('/admin/deposits/pending'),

  /** The "All" tab — every deposit, newest first, optionally filtered by status. */
  deposits: (
    input: { limit?: number; offset?: number; status?: 'submitted' | 'under_review' | 'approved' | 'rejected' } = {},
  ): Promise<{ deposits: AdminDepositView[]; total: number }> => {
    const params = new URLSearchParams();
    if (input.limit) params.set('limit', String(input.limit));
    if (input.offset) params.set('offset', String(input.offset));
    if (input.status) params.set('status', input.status);
    const qs = params.toString();
    return request(`/admin/deposits${qs ? `?${qs}` : ''}`);
  },

  deposit: (id: string): Promise<AdminDepositDetailView> => request(`/admin/deposits/${id}`),

  startDepositReview: (id: string): Promise<AdminDepositView> =>
    request(`/admin/deposits/${id}/review`, { method: 'POST' }),

  /**
   * May return the deposit still `under_review` — above the two-person
   * threshold this records an approval and posts nothing until a second,
   * distinct admin approves (§7.2). Callers must read the returned status
   * rather than assume a resolved promise moved money.
   */
  /**
   * `settledAmount` (santim) is the figure the admin verified themselves —
   * required when no Telebirr SMS confirms the reference, and ignored in
   * favour of the SMS when one does.
   */
  approveDeposit: (
    id: string,
    note?: string,
    settledAmount?: string,
  ): Promise<AdminDepositDetailView> =>
    request(`/admin/deposits/${id}/approve`, {
      method: 'POST',
      body: {
        ...(note ? { note } : {}),
        ...(settledAmount ? { settledAmount } : {}),
      },
    }),

  rejectDeposit: (id: string, reason: string): Promise<AdminDepositView> =>
    request(`/admin/deposits/${id}/reject`, { method: 'POST', body: { reason } }),

  // --- withdrawals ---------------------------------------------------------

  pendingWithdrawals: (): Promise<{ withdrawals: AdminWithdrawalView[] }> =>
    request('/admin/withdrawals/pending'),

  withdrawal: (id: string): Promise<AdminWithdrawalView> => request(`/admin/withdrawals/${id}`),

  approveWithdrawal: (id: string): Promise<AdminWithdrawalView> =>
    request(`/admin/withdrawals/${id}/approve`, { method: 'POST' }),

  /**
   * Every withdrawal, newest first. `pendingWithdrawals` drops a payout the
   * moment it settles, so this is the only place a completed or confirmed one
   * can be found.
   */
  withdrawals: (params: { limit?: number; offset?: number; status?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    if (params.offset !== undefined) q.set('offset', String(params.offset));
    if (params.status !== undefined) q.set('status', params.status);
    const qs = q.toString();
    return request(`/admin/withdrawals${qs ? `?${qs}` : ''}`) as Promise<{
      withdrawals: AdminWithdrawalView[];
      total: number;
    }>;
  },

  processWithdrawal: (id: string): Promise<AdminWithdrawalView> =>
    request(`/admin/withdrawals/${id}/process`, { method: 'POST' }),

  /**
   * Posts the bank leg. Pressed only after the money has actually left, and
   * `txReference` is the bank's own transaction number for that transfer —
   * required, so a completed payout is always checkable against a statement.
   */
  completeWithdrawal: (id: string, txReference: string): Promise<AdminWithdrawalView> =>
    request(`/admin/withdrawals/${id}/complete`, { method: 'POST', body: { txReference } }),

  failWithdrawal: (id: string, reason: string): Promise<AdminWithdrawalView> =>
    request(`/admin/withdrawals/${id}/fail`, { method: 'POST', body: { reason } }),

  rejectWithdrawal: (id: string, reason: string): Promise<AdminWithdrawalView> =>
    request(`/admin/withdrawals/${id}/reject`, { method: 'POST', body: { reason } }),

  // --- rounds --------------------------------------------------------------

  patterns: (): Promise<{ patterns: WinningPattern[] }> => request('/admin/patterns'),

  gameTypes: (): Promise<{ gameTypes: GameTypeView[] }> => request('/admin/game-types'),

  updateGameType: (id: string, input: UpdateGameTypeInput): Promise<GameTypeView> =>
    request(`/admin/game-types/${id}`, { method: 'PATCH', body: input }),

  startRound: (id: string): Promise<RoundView> =>
    request(`/admin/rounds/${id}/start`, { method: 'POST' }),

  round: (id: string): Promise<RoundView> => request(`/admin/rounds/${id}`),

  rounds: (
    gameTypeId: string | null,
    opts: { limit?: number; offset?: number } = {},
  ): Promise<{ rounds: RoundListItemView[] }> => {
    const params = new URLSearchParams();
    if (gameTypeId) params.set('gameTypeId', gameTypeId);
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.offset !== undefined) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return request(`/admin/rounds${qs ? `?${qs}` : ''}`);
  },

  drawNext: (id: string): Promise<DrawResult> =>
    request(`/admin/rounds/${id}/draw`, { method: 'POST' }),

  cancelRound: (id: string, reason: string): Promise<RoundView> =>
    request(`/admin/rounds/${id}/cancel`, { method: 'POST', body: { reason } }),

  // --- payment destinations ------------------------------------------------

  destinations: (): Promise<{ destinations: AdminPaymentDestinationView[] }> =>
    request('/admin/payment-destinations'),

  createDestination: (input: CreateDestinationInput): Promise<AdminPaymentDestinationView> =>
    request('/admin/payment-destinations', { method: 'POST', body: input }),

  setDestinationActive: (id: string, active: boolean): Promise<AdminPaymentDestinationView> =>
    request(`/admin/payment-destinations/${id}/active`, { method: 'POST', body: { active } }),

  updateDestination: (id: string, input: UpdateDestinationInput): Promise<AdminPaymentDestinationView> =>
    request(`/admin/payment-destinations/${id}`, { method: 'PATCH', body: input }),

  // --- admins --------------------------------------------------------------

  createAdmin: (input: {
    username: string;
    password: string;
    role: string;
  }): Promise<{ admin: AdminView; totpSecret: string; otpauthUrl: string }> =>
    request('/admin/admins', { method: 'POST', body: input }),

  revokeSessions: (adminId: string): Promise<{ revoked: number }> =>
    request(`/admin/admins/${adminId}/revoke-sessions`, { method: 'POST' }),

  // --- controls ------------------------------------------------------------

  reconcile,

  audit: (entityType: AuditEntityType, entityId: string): Promise<{ entries: AuditEntry[] }> =>
    request(`/admin/audit/${entityType}/${entityId}`),

  // --- players -------------------------------------------------------------

  players: (
    input: {
      q?: string;
      limit?: number;
      offset?: number;
      winnersOnly?: boolean;
      status?: 'active' | 'suspended' | 'closed';
      /** `true` for the Phantom tab (phantom test accounts only); omit/`false` excludes them. */
      phantom?: boolean;
    } = {},
  ): Promise<{
    players: AdminPlayerView[];
    total: number;
  }> => {
    const params = new URLSearchParams();
    if (input.q) params.set('q', input.q);
    if (input.limit) params.set('limit', String(input.limit));
    if (input.offset) params.set('offset', String(input.offset));
    if (input.winnersOnly) params.set('winners', 'true');
    if (input.status) params.set('status', input.status);
    if (input.phantom) params.set('phantom', 'true');
    const qs = params.toString();
    return request(`/admin/users${qs ? `?${qs}` : ''}`);
  },

  player: (id: string): Promise<AdminPlayerDetailView> => request(`/admin/users/${id}`),

  setPlayerStatus: (id: string, status: 'active' | 'suspended' | 'closed'): Promise<AdminPlayerView> =>
    request(`/admin/users/${id}/status`, { method: 'PATCH', body: { status } }),

  deletePlayer: (id: string): Promise<{ ok: boolean }> =>
    request(`/admin/users/${id}`, { method: 'DELETE' }),

  // --- IP allowlist (§7.6) --------------------------------------------------

  ipAllowlist: (): Promise<{ env: string[]; entries: AdminIpAllowlistEntryView[] }> =>
    request('/admin/ip-allowlist'),

  addIpAllowlist: (ip: string, note?: string): Promise<{ entry: AdminIpAllowlistEntryView }> =>
    request('/admin/ip-allowlist', { method: 'POST', body: note ? { ip, note } : { ip } }),

  removeIpAllowlist: (ip: string): Promise<{ removed: string }> =>
    request(`/admin/ip-allowlist?ip=${encodeURIComponent(ip)}`, { method: 'DELETE' }),

  // --- agents (migration 070) ----------------------------------------------

  agents: (): Promise<AgentView[]> => request('/admin/agents'),

  /** `null` stops someone being an agent; what they already earned stands. */
  setAgentRate: (userId: string, rateBps: number | null): Promise<void> =>
    request(`/admin/agents/${userId}/rate`, { method: 'POST', body: { rateBps } }),

  /**
   * Records an out-of-band payment. `total` is what was actually sent, in
   * santim — the server refuses a figure that disagrees with the outstanding
   * balance rather than reconciling it.
   */
  markAgentPaid: (userId: string, total: string): Promise<{ rounds: number; total: string }> =>
    request(`/admin/agents/${userId}/paid`, { method: 'POST', body: { total } }),

  agentBots: (): Promise<AgentBotView[]> => request('/admin/agent-bots'),

  agentCollections: (): Promise<AgentCollectionView[]> => request('/admin/agent-collections'),

  /** The signing key goes up and never comes back, like a bot token. */
  saveAgentCollection: (input: {
    agentId: string;
    telebirrAccount: string;
    accountName?: string | null;
    smsWebhookSecret: string;
    dailyCreditCap: string;
    floatCeiling: string;
  }): Promise<{ agentId: string }> =>
    request('/admin/agent-collections', { method: 'POST', body: input }),

  remitAgentFloat: (
    agentId: string,
    amount: string,
    txReference?: string,
  ): Promise<{ amount: string; outstandingAfter: string }> =>
    request(`/admin/agent-collections/${agentId}/remit`, {
      method: 'POST',
      body: txReference ? { amount, txReference } : { amount },
    }),

  setAgentFrozen: (agentId: string, frozen: boolean, reason?: string): Promise<void> =>
    request(`/admin/agent-collections/${agentId}/frozen`, {
      method: 'POST',
      body: reason ? { frozen, reason } : { frozen },
    }),

  /**
   * Saves an agent's bot. The token goes up and never comes back — the list
   * above does not return it.
   */
  saveAgentBot: (input: {
    agentId: string;
    slug: string;
    botToken: string;
    botUsername: string;
    miniAppName?: string | null;
  }): Promise<AgentBotView> => request('/admin/agent-bots', { method: 'POST', body: input }),

  setAgentBotActive: (agentId: string, active: boolean): Promise<void> =>
    request(`/admin/agent-bots/${agentId}/active`, { method: 'POST', body: { active } }),

  // --- one-shot enrolment codes (§7.6 bootstrap) ----------------------------

  /**
   * Only state comes back from the list — never the codes themselves — so
   * reading it cannot leak a still-valid one.
   */
  enrolmentCodes: (): Promise<{ codes: EnrolmentCodeView[] }> =>
    request('/admin/enrolment-codes'),

  /** Mints a code. The returned plaintext is shown exactly once, at mint. */
  mintEnrolmentCode: (): Promise<MintedEnrolmentCode> =>
    request('/admin/enrolment-codes', { method: 'POST' }),

  /** Voids an unused code — the «I changed my mind / it leaked» button. */
  revokeEnrolmentCode: (id: string): Promise<{ revoked: string }> =>
    request(`/admin/enrolment-codes/${id}`, { method: 'DELETE' }),

  // --- media assets (posters/ads) -------------------------------------------

  mediaAssets: (kind?: MediaAssetKind): Promise<{ assets: MediaAssetView[] }> =>
    request(`/admin/media-assets${kind ? `?kind=${kind}` : ''}`),

  /**
   * The first multipart call anywhere in this console — bespoke, not
   * `request()`, for the same reason `readProofBytes` on the player side is
   * separate from the JSON client: `request()` always sets
   * `content-type: application/json`, and a `FormData` body needs the
   * browser to set its own multipart boundary, which happens only when no
   * `content-type` header is set by hand.
   *
   * Field order matters here: `kind`/`caption` are appended *before* the
   * file, because the API reads them off `request.file().fields`, which
   * only has fields populated that arrived earlier in the same multipart
   * body.
   */
  uploadMediaAsset: async (
    file: File,
    kind: MediaAssetKind,
    caption?: string,
  ): Promise<MediaAssetView> => {
    const token = getToken();
    if (!token) throw new ApiError(401, null, 'not signed in');

    const form = new FormData();
    form.set('kind', kind);
    if (caption) form.set('caption', caption);
    form.set('file', file);

    const trustedDevice = getTrustedDevice();
    const response = await postMultipart(BASE + '/admin/media-assets', {
      authorization: `Bearer ${token}`,
      ...(trustedDevice ? { 'x-trusted-device': trustedDevice } : {}),
    }, form);

    const text = await response.text();
    const parsed = text ? (JSON.parse(text) as unknown) : null;
    if (!response.ok) {
      const error = new ApiError(response.status, parsed as ApiErrorBody | null, response.statusText);
      if (error.isAuthFailure) clearToken();
      throw error;
    }
    renewTrustedDeviceFrom(response);
    return parsed as MediaAssetView;
  },

  /**
   * Bytes for the console's own thumbnail preview, as a `Blob` — turned into
   * an object URL by the caller. Not a plain `<img src>` to a server path:
   * this route requires the same bearer token every other admin call does,
   * which an `<img>` tag has no way to send.
   */
  mediaAssetBlob: async (id: string): Promise<Blob> => {
    const token = getToken();
    if (!token) throw new ApiError(401, null, 'not signed in');
    const trustedDevice = getTrustedDevice();
    let response: Response;
    try {
      response = await fetchWithTimeout(
        BASE + `/admin/media-assets/${id}/bytes`,
        {
          headers: {
            authorization: `Bearer ${token}`,
            ...(trustedDevice ? { 'x-trusted-device': trustedDevice } : {}),
          },
        },
        BLOB_TIMEOUT_MS,
      );
    } catch {
      throw new ApiError(0, null, 'could not reach the API');
    }
    if (!response.ok) throw new ApiError(response.status, null, response.statusText);
    renewTrustedDeviceFrom(response);
    try {
      // The deadline above covers headers; the bytes can stall separately on the
      // same half-dead socket (the download equivalent of a dropped upload).
      return await response.blob();
    } catch {
      throw new ApiError(0, null, 'could not reach the API');
    }
  },

  setMediaAssetActive: (id: string, active: boolean): Promise<MediaAssetView> =>
    request(`/admin/media-assets/${id}/active`, { method: 'POST', body: { active } }),

  deleteMediaAsset: (id: string): Promise<void> =>
    request(`/admin/media-assets/${id}`, { method: 'DELETE' }),

  /**
   * Caption and/or the one-shot manual schedule. `scheduledAt: null` clears
   * a standing schedule; omitting either field leaves it untouched — so a
   * caption-only edit doesn't have to also resend the current schedule.
   */
  updateMediaAsset: (
    id: string,
    fields: { caption?: string | null; scheduledAt?: string | null },
  ): Promise<MediaAssetView> =>
    request(`/admin/media-assets/${id}`, { method: 'PATCH', body: fields }),

  /** Swaps a poster's file in place — same shape as `uploadMediaAsset`, one file, no other fields. */
  replaceMediaAsset: async (id: string, file: File): Promise<MediaAssetView> => {
    const token = getToken();
    if (!token) throw new ApiError(401, null, 'not signed in');

    const form = new FormData();
    form.set('file', file);

    const trustedDevice = getTrustedDevice();
    const response = await postMultipart(BASE + `/admin/media-assets/${id}/replace`, {
      authorization: `Bearer ${token}`,
      ...(trustedDevice ? { 'x-trusted-device': trustedDevice } : {}),
    }, form);

    const text = await response.text();
    const parsed = text ? (JSON.parse(text) as unknown) : null;
    if (!response.ok) {
      const error = new ApiError(response.status, parsed as ApiErrorBody | null, response.statusText);
      if (error.isAuthFailure) clearToken();
      throw error;
    }
    renewTrustedDeviceFrom(response);
    return parsed as MediaAssetView;
  },

  /** Sends this poster to every real player right now. */
  postMediaAssetNow: (id: string): Promise<void> =>
    request(`/admin/media-assets/${id}/post-now`, { method: 'POST' }),

  /** What the payout dispatcher has tried on this withdrawal, newest first. */
  payoutAttempts: (id: string): Promise<{ attempts: PayoutAttemptView[] }> =>
    request(`/admin/withdrawals/${id}/payout-attempts`),

  // --- dashboard --------------------------------------------------------------

  revenueReport: (months?: number): Promise<RevenueReport> =>
    request(`/admin/dashboard/revenue${months ? `?months=${months}` : ''}`),

  // --- inbound player messages -------------------------------------------------

  /** What players typed at the bot that no command answered — the support inbox. */
  // --- support inbox ---------------------------------------------------------

  supportThreads: (limit = 100): Promise<{ threads: SupportThreadView[] }> =>
    request(`/admin/support/threads?limit=${limit}`),

  supportThread: (telegramId: string): Promise<{ messages: SupportMessageView[] }> =>
    request(`/admin/support/threads/${telegramId}`),

  /** Sends as the bot, into the player's own chat. */
  replyToSupportThread: (telegramId: string, message: string): Promise<SupportMessageView> =>
    request(`/admin/support/threads/${telegramId}/reply`, { method: 'POST', body: { message } }),

  // --- system status broadcast -----------------------------------------------

  systemNotice: (status: 'down' | 'up', message?: string): Promise<void> =>
    request('/admin/system-notice', {
      method: 'POST',
      body: message ? { status, message } : { status },
    }),

  // --- admin broadcasts (message + optional media, now or scheduled) --------

  broadcasts: (): Promise<{ broadcasts: BroadcastView[] }> => request('/admin/broadcasts'),

  /**
   * Composes and sends (or schedules) a broadcast. Multipart, same shape as
   * `uploadMediaAsset` — `file` is optional here, a text-only broadcast is
   * the common case. `scheduledAt` omitted or empty sends immediately.
   */
  createBroadcast: async (
    message: string,
    file?: File | null,
    scheduledAt?: string | null,
  ): Promise<BroadcastView> => {
    const token = getToken();
    if (!token) throw new ApiError(401, null, 'not signed in');

    const form = new FormData();
    form.set('message', message);
    if (scheduledAt) form.set('scheduledAt', scheduledAt);
    if (file) form.set('file', file);

    const trustedDevice = getTrustedDevice();
    let response: Response;
    try {
      response = await fetchWithTimeout(
        BASE + '/admin/broadcasts',
        {
          method: 'POST',
          headers: {
            authorization: `Bearer ${token}`,
            ...(trustedDevice ? { 'x-trusted-device': trustedDevice } : {}),
          },
          body: form,
        },
        uploadTimeoutMs(file?.size ?? 0),
      );
    } catch {
      throw new ApiError(0, null, 'could not reach the API');
    }

    const text = await response.text();
    const parsed = text ? (JSON.parse(text) as unknown) : null;
    if (!response.ok) {
      const error = new ApiError(response.status, parsed as ApiErrorBody | null, response.statusText);
      if (error.isAuthFailure) clearToken();
      throw error;
    }
    return parsed as BroadcastView;
  },
};
